import { datumKratko } from "@/lib/format";

/**
 * Badge text and tone for the three statuses the personnel list shows.
 *
 * Pure functions, deliberately separate from the components: these encode
 * regulatory thresholds, and a threshold buried in JSX is a threshold nobody
 * tests. A badge that says "Uredno" on the day a permit expires is a worker on
 * a site without a permit — 5.000–20.000 € and the employer blacklist.
 *
 * Every badge carries a word. Colour only reinforces it.
 */

export type Ton = "ok" | "warn" | "bad" | "idle";

export interface Oznaka {
  tekst: string;
  ton: Ton;
}

/** Renewal has to start this many days before expiry. */
export const PRAG_UPOZORENJA = 30;
/** Seasonal work: the regulation allows the shorter run-up. */
export const PRAG_UPOZORENJA_SEZONSKI = 15;
/** Escalation. Past this the badge stops being a note and becomes urgent. */
export const PRAG_ESKALACIJE = 5;

export interface StatusDozvole {
  stanje:
    | "nepoznato"
    | "nije_potrebno"
    | "nema"
    | "uredno"
    | "istice"
    | "u_postupku"
    | "isteklo";
  datum_isteka: string | null;
  dana_do_isteka: number | null;
  produljenje_podneseno: boolean;
  kriticno: boolean;
}

export interface ZdravstveniStatus {
  smije_raditi: boolean;
  vrijedi_do: string | null;
}

export interface Uskladenost {
  potpuno: boolean;
  nedostaje: string[];
}

export function pragUpozorenja(vrstaUgovora?: string | null): number {
  return vrstaUgovora === "sezonski"
    ? PRAG_UPOZORENJA_SEZONSKI
    : PRAG_UPOZORENJA;
}

/** Days between two ISO dates, ignoring the clock. */
export function danaDo(iso: string, danas: Date): number {
  const cilj = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const pocetak = new Date(
    danas.getFullYear(),
    danas.getMonth(),
    danas.getDate(),
  );
  return Math.round((cilj.getTime() - pocetak.getTime()) / 86_400_000);
}

/**
 * The permit badge.
 *
 * A renewal filed in time keeps the work lawful until the decision is final
 * (NN 55/26), so it is a note rather than an alarm — raising one anyway is how
 * people learn to ignore the alarm that matters.
 */
export function oznakaDozvole(
  status: StatusDozvole | null | undefined,
  vrstaUgovora?: string | null,
): Oznaka {
  if (!status) return { tekst: "Nije predano", ton: "idle" };

  switch (status.stanje) {
    case "nepoznato":
      return { tekst: "Upišite državljanstvo", ton: "warn" };
    case "nije_potrebno":
      return { tekst: "Nije potrebno", ton: "idle" };
    case "nema":
      return { tekst: "Nema dozvole", ton: "bad" };
    case "isteklo":
      return { tekst: "Isteklo", ton: "bad" };
    case "u_postupku":
      return { tekst: "Zahtjev podnesen", ton: "warn" };
    case "uredno":
      return { tekst: "Uredno", ton: "ok" };
    case "istice": {
      const dana = status.dana_do_isteka ?? 0;
      if (dana <= PRAG_ESKALACIJE) {
        return {
          tekst: dana === 0 ? "Ističe danas" : `Ističe za ${dana} d.`,
          ton: "bad",
        };
      }
      if (dana <= pragUpozorenja(vrstaUgovora)) {
        return { tekst: `Ističe ${datumKratko(status.datum_isteka)}`, ton: "warn" };
      }
      return { tekst: "Uredno", ton: "ok" };
    }
  }
}

/**
 * The medical badge.
 *
 * A worker judged unfit must not be on site (ZNR čl. 36), so that is `bad`
 * whatever the dates say. "Nije predano" is neutral: no certificate is a gap in
 * the file, not a verdict about the person.
 */
export function oznakaLijecnickog(
  status: ZdravstveniStatus | null | undefined,
  danas: Date = new Date(),
): Oznaka {
  if (!status) return { tekst: "Nije predano", ton: "idle" };
  if (!status.smije_raditi) return { tekst: "Ne smije raditi", ton: "bad" };
  if (!status.vrijedi_do) return { tekst: "Uredno", ton: "ok" };

  const dana = danaDo(status.vrijedi_do, danas);
  if (dana < 0) return { tekst: "Istekao", ton: "bad" };
  if (dana <= PRAG_ESKALACIJE) {
    return {
      tekst: dana === 0 ? "Ističe danas" : `Ističe za ${dana} d.`,
      ton: "bad",
    };
  }
  if (dana <= PRAG_UPOZORENJA) {
    return { tekst: `Ističe ${datumKratko(status.vrijedi_do)}`, ton: "warn" };
  }
  return { tekst: "Uredno", ton: "ok" };
}

/** How much of the mandatory record is still missing (§9). */
export function oznakaUskladenosti(
  uskladenost: Uskladenost | null | undefined,
): Oznaka {
  if (!uskladenost) return { tekst: "Nije predano", ton: "idle" };
  if (uskladenost.potpuno) return { tekst: "Potpuno", ton: "ok" };
  return {
    tekst: `Nepotpuno (${uskladenost.nedostaje.length})`,
    ton: "warn",
  };
}

/**
 * Whether the whole row is tinted: this worker must not be on site today.
 *
 * Only the two states that actually block work. Tinting rows for anything that
 * merely needs attention would make the tint mean "row" rather than "stop".
 */
export function kriticanRedak(
  dozvola: StatusDozvole | null | undefined,
  zdravlje: ZdravstveniStatus | null | undefined,
): boolean {
  return Boolean(dozvola?.kriticno) || zdravlje?.smije_raditi === false;
}
